import { useEffect, useRef, useState } from "react";

export type AxisSubKind = "weights" | "threshold";

export interface AjustarPanelProps {
  kind: "evidence" | "axis" | "skill";
  target: string;
  current: number | string;
  scale: "0-5" | "0-1" | "L1-L5";
  onSave: (
    newScore: number,
    rationale: string,
    opts: { policy: "auto" | "preview" | "park"; axisSubKind?: AxisSubKind },
  ) => Promise<void> | void;
  onCancel: () => void;
  /** Retained for backwards compatibility — no longer changes behaviour.
   *  Every correction is parked for the batch calibrator now. */
  allowPreview?: boolean;
}

const KIND_LABEL = {
  evidence: "Ajuste de evidência",
  axis: "Ajuste de decisão de eixo",
  skill: "Ajuste de nível de skill",
} as const;

const PLACEHOLDER = {
  evidence:
    "Ex.: O modelo não pegou que a referência confirmou 14 meses, não 2 anos. Aponte o sinal perdido.",
  axis: "Ex.: A decisão ignorou o salto recente de escopo. Explique que critério o modelo deve priorizar.",
  skill:
    "Ex.: O candidato lidera rituais de PM mas o modelo puniu a ausência de certificação. Corrija a rubrica.",
} as const;

const WHY_LABEL = {
  evidence: "Por que a nota do modelo estava errada?",
  axis: "Por que esta decisão de eixo estava errada?",
  skill: "Por que o nível atribuído estava errado?",
} as const;

const MIN_RATIONALE_CHARS = 20;

function toNumber(v: number | string): number {
  if (typeof v === "number") return v;
  const m = /^L([1-5])$/.exec(v);
  if (m) return Number(m[1]);
  return Number(v);
}

function formatBall(v: number): string {
  return v % 1 === 0 ? v.toFixed(1) : v.toFixed(1);
}

function formatForScale(v: number, scale: AjustarPanelProps["scale"]): string {
  if (scale === "L1-L5") return `L${v}`;
  if (scale === "0-1") return v.toFixed(2);
  return formatBall(v);
}

function formatDelta(v: number, scale: AjustarPanelProps["scale"]): string {
  const sign = v > 0 ? "+" : v < 0 ? "−" : "±";
  const abs = Math.abs(v);
  if (scale === "0-1") return `${sign}${abs.toFixed(2)}`;
  if (scale === "L1-L5") return `${sign}${abs.toFixed(0)}`;
  return `${sign}${abs.toFixed(1)}`;
}

export function AjustarPanel({
  kind,
  target,
  current,
  scale,
  onSave,
  onCancel,
  // allowPreview is retained on the interface for back-compat but the
  // panel no longer renders a preview toggle — everything parks.
  allowPreview: _allowPreview = false,
}: AjustarPanelProps) {
  const originalNumeric = toNumber(current);
  const [selected, setSelected] = useState<number>(originalNumeric);
  const [rationale, setRationale] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [axisSubKind, setAxisSubKind] = useState<AxisSubKind>("weights");
  // Step 2 (rationale + actions) reveals once the user has picked any value
  // different from the original, and stays revealed even if they revert.
  // This enforces the desired flow: "pick a new grade → then explain why".
  const [stage, setStage] = useState<1 | 2>(1);
  // Nudge the "original" readout when the user clicks a grade that matches
  // the original in stage 2 — they need to know why save is still disabled.
  const [revertFlash, setRevertFlash] = useState(false);
  const revertTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Highlight the rationale field when the user tries to submit without it.
  const [rationaleMissed, setRationaleMissed] = useState(false);

  useEffect(
    () => () => {
      if (revertTimer.current) clearTimeout(revertTimer.current);
    },
    [],
  );

  const valueLabel = kind === "skill" ? "Proficiência correta (1–5)" : "Nota correta";
  const kindLabel = KIND_LABEL[kind];
  const showDotTierHint = kind === "skill" && scale === "0-5";

  const changed = Math.abs(selected - originalNumeric) > 0.001;
  const rationaleFilled = rationale.trim().length > 0;
  const rationaleCount = rationale.trim().length;
  const rationaleMeetsMin = rationaleCount >= MIN_RATIONALE_CHARS;

  const handleSelect = (v: number) => {
    setSelected(v);
    const isChange = Math.abs(v - originalNumeric) > 0.001;
    if (isChange && stage === 1) setStage(2);
    if (!isChange && stage === 2) {
      // Stay in stage 2 but flash the "valor igual ao original" hint.
      setRevertFlash(true);
      if (revertTimer.current) clearTimeout(revertTimer.current);
      revertTimer.current = setTimeout(() => setRevertFlash(false), 1200);
    }
  };

  const handleSave = async () => {
    if (!changed) return;
    if (!rationaleFilled) {
      setRationaleMissed(true);
      return;
    }
    setSubmitting(true);
    try {
      // Every correction is parked now — the batch-calibrate worker picks up
      // the backlog together, running all parked corrections against the full
      // corpus in one pass with the thinking-model for patch proposal.
      await onSave(selected, rationale, {
        policy: "park",
        ...(kind === "axis" ? { axisSubKind } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const currentLabel = formatForScale(originalNumeric, scale);
  const selectedLabel = formatForScale(selected, scale);
  const deltaValue = selected - originalNumeric;
  const deltaLabel = formatDelta(deltaValue, scale);
  const deltaDir = deltaValue > 0 ? "up" : deltaValue < 0 ? "down" : "flat";

  // One disabled reason, evaluated in order of priority. Guarantees the user
  // never sees an inert button without knowing why.
  const disabledReason: "no-change" | "no-rationale" | null = !changed
    ? "no-change"
    : !rationaleFilled
      ? "no-rationale"
      : null;

  const disabledMessage =
    disabledReason === "no-change"
      ? "Ajuste a pontuação antes de salvar — o valor ainda é o original."
      : disabledReason === "no-rationale"
        ? "A explicação é obrigatória — conte por que o modelo errou."
        : null;

  const saveLabel = submitting ? "Salvando" : "Salvar ajuste";

  return (
    <form
      aria-label={`${kindLabel}: ${target}`}
      data-testid="ajustar-panel"
      className={`adjust-panel stage-${stage}`}
      onSubmit={(e) => e.preventDefault()}
    >
      {/* Named step rail — replaces the anonymous 2-dot badge. Each step
       * carries an explicit label so the CHRO knows exactly where they are
       * in the correction ritual. */}
      <nav className="ap-steprail" data-testid="ap-stage-badge" aria-label={`etapa ${stage} de 2`}>
        <Step n={1} label="escolha a nota" active={stage === 1} done={stage === 2} />
        <span className={`ap-steprail-line${stage === 2 ? " on" : ""}`} aria-hidden />
        <Step n={2} label="explique o motivo" active={stage === 2} done={false} />
      </nav>

      <div className="ap-head">
        <span className="ap-kind">{kindLabel}</span>
        <span className="ap-target">
          {target} <span className="ap-current">atual: {currentLabel}</span>
        </span>
      </div>

      {kind === "axis" && (
        <fieldset className="ap-axis-subkind" data-testid="ap-axis-subkind">
          <legend className="ap-label">Esse ajuste corrige</legend>
          <label>
            <input
              type="radio"
              name="axis-sub-kind"
              value="weights"
              checked={axisSubKind === "weights"}
              onChange={() => setAxisSubKind("weights")}
              data-testid="axis-sub-weights"
            />
            <span>os pesos das evidências</span>
          </label>
          <label>
            <input
              type="radio"
              name="axis-sub-kind"
              value="threshold"
              checked={axisSubKind === "threshold"}
              onChange={() => setAxisSubKind("threshold")}
              data-testid="axis-sub-threshold"
            />
            <span>o corte de aprovação</span>
          </label>
        </fieldset>
      )}

      {stage === 1 && (
        <p className="ap-stage1-ask" data-testid="ap-stage1-prompt">
          Qual é a nota correta? <span className="ap-stage1-ask-sub">comece por aqui.</span>
        </p>
      )}

      <label className="ap-label" htmlFor="ajustar-picker">
        {valueLabel}
      </label>
      {showDotTierHint && <DotTierHint selected={selected} />}
      <div className="picker-slot" id="ajustar-picker">
        {scale === "0-5" && (
          <BallPicker current={originalNumeric} selected={selected} onChange={handleSelect} />
        )}
        {scale === "0-1" && (
          <SliderPicker current={originalNumeric} selected={selected} onChange={handleSelect} />
        )}
        {scale === "L1-L5" && (
          <LevelPicker current={originalNumeric} selected={selected} onChange={handleSelect} />
        )}
      </div>

      {/* When the user lands on stage 2 with the grade reverted to the
       * original, anchor the disabled-reason next to the picker — that's
       * where the fix is. */}
      {stage === 2 && disabledReason === "no-change" && (
        <p
          className={`ap-inline-hint near-picker${revertFlash ? " flash" : ""}`}
          data-testid="ap-disabled-hint"
          role="status"
        >
          <span aria-hidden className="ap-inline-hint-arrow">
            ↑
          </span>
          {disabledMessage}
        </p>
      )}

      {/* Stage 2 — only revealed once the user has picked a different value.
       * Once revealed, it stays (even if they revert the grade), so the
       * save button can surface a disabled-with-reason state. */}
      {stage === 2 && (
        <div className="ap-stage2" data-testid="ap-stage2">
          <DeltaHero
            original={currentLabel}
            next={selectedLabel}
            delta={deltaLabel}
            direction={deltaDir}
          />

          <label className="ap-label ap-required-label" htmlFor="ajustar-rationale">
            <span className="ap-asterisk" aria-hidden>
              *
            </span>
            {WHY_LABEL[kind]} <span className="ap-required">obrigatório</span>
          </label>
          <div
            className={`ap-textarea-wrap${
              rationaleFilled ? " filled" : ""
            }${rationaleMissed && !rationaleFilled ? " missed" : ""}`}
          >
            <textarea
              id="ajustar-rationale"
              className={rationaleMissed && !rationaleFilled ? "ap-textarea missed" : "ap-textarea"}
              placeholder={PLACEHOLDER[kind]}
              value={rationale}
              aria-required="true"
              aria-invalid={rationaleMissed && !rationaleFilled ? "true" : undefined}
              onChange={(e) => {
                setRationale(e.target.value);
                if (rationaleMissed && e.target.value.trim().length > 0) {
                  setRationaleMissed(false);
                }
              }}
            />
          </div>
          <div className="ap-textarea-foot">
            <span
              className={`ap-char-count${rationaleMeetsMin ? " met" : ""}`}
              data-testid="ap-char-count"
            >
              {rationaleCount} char · mín. {MIN_RATIONALE_CHARS}
            </span>
            {disabledReason === "no-rationale" && (
              <span
                className="ap-inline-hint near-textarea"
                data-testid="ap-disabled-hint"
                role="status"
              >
                <span aria-hidden className="ap-inline-hint-arrow">
                  ↑
                </span>
                {disabledMessage}
              </span>
            )}
          </div>

          <p className="ap-park-hint" data-testid="ap-park-hint">
            Sua correção fica registrada. De tempos em tempos juntamos todas as correções recentes e
            recalibramos o modelo de uma vez só, e com mais cuidado do que aplicar um ajuste solto.
          </p>

          <div className="ap-actions">
            <button type="button" className="ap-btn" onClick={onCancel} disabled={submitting}>
              cancelar
            </button>
            <div className="ap-save-wrap">
              <button
                type="button"
                className={`ap-btn primary${disabledReason ? " inert" : ""}`}
                onClick={handleSave}
                disabled={submitting || !!disabledReason}
                aria-disabled={!!disabledReason}
                title={disabledMessage ?? undefined}
                data-testid="ap-save"
              >
                <span className="ap-btn-label">
                  {saveLabel}
                  {submitting && (
                    <span className="ellipsis" aria-hidden>
                      …
                    </span>
                  )}
                </span>
                {disabledReason && (
                  <span className="ap-btn-sub" aria-hidden>
                    {disabledReason === "no-change"
                      ? "ajuste uma nota diferente"
                      : "preencha a explicação"}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {stage === 1 && (
        <div className="ap-stage1-foot">
          <button type="button" className="ap-btn" onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
        </div>
      )}
    </form>
  );
}

function Step({
  n,
  label,
  active,
  done,
}: {
  n: 1 | 2;
  label: string;
  active: boolean;
  done: boolean;
}) {
  const state = done ? "done" : active ? "active" : "pending";
  return (
    <div className={`ap-step ${state}`} aria-current={active ? "step" : undefined}>
      <span className="ap-step-marker" aria-hidden>
        {done ? "ok" : n}
      </span>
      <span className="ap-step-label">{label}</span>
    </div>
  );
}

function DeltaHero({
  original,
  next,
  delta,
  direction,
}: {
  original: string;
  next: string;
  delta: string;
  direction: "up" | "down" | "flat";
}) {
  return (
    <div className={`ap-delta-hero dir-${direction}`} data-testid="ap-delta-hero">
      <div className="ap-delta-row">
        <span className="ap-delta-from">
          <span className="ap-delta-label">original</span>
          <span className="ap-delta-num">{original}</span>
        </span>
        <span className="ap-delta-arrow" aria-hidden>
          →
        </span>
        <span className="ap-delta-to">
          <span className="ap-delta-label">sua correção</span>
          <span className="ap-delta-num">{next}</span>
        </span>
        <span className={`ap-delta-badge dir-${direction}`}>{delta}</span>
      </div>
    </div>
  );
}

interface PickerProps {
  current: number;
  selected: number;
  onChange: (v: number) => void;
}

function BallStepChevron({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      className={`ball-step-chev ball-step-chev-${direction}`}
      viewBox="0 0 12 12"
      width="11"
      height="11"
      role="img"
      aria-label={direction === "left" ? "diminuir" : "aumentar"}
    >
      <path
        d="M8 2L4 6L8 10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BallPicker({ current, selected, onChange }: PickerProps) {
  const dots = [1, 2, 3, 4, 5];
  const isZeroSelected = selected <= 0.001;
  const nearestAnchor = Math.round(selected * 2) / 2;
  const offAnchor = Math.abs(selected - nearestAnchor) > 0.001;
  const stepDown = () => onChange(Math.max(0, Math.round((selected - 0.1) * 10) / 10));
  const stepUp = () => onChange(Math.min(5, Math.round((selected + 0.1) * 10) / 10));
  const atMin = selected <= 0.001;
  const atMax = selected >= 5 - 0.001;
  // The two adjacent half-step anchors that bracket the current value. Used by
  // the off-anchor track to show "where between 3.5 and 4.0" the value sits.
  const lowerAnchor = Math.max(0, Math.floor(selected * 2) / 2);
  const upperAnchor = Math.min(5, lowerAnchor + 0.5);
  const trackPct =
    upperAnchor > lowerAnchor
      ? Math.max(0, Math.min(1, (selected - lowerAnchor) / (upperAnchor - lowerAnchor)))
      : 0;
  return (
    <div className="ball-picker">
      <div className="ball-track">
        <button
          type="button"
          className={`pick-zero${isZeroSelected ? " selected" : ""}`}
          data-testid="pick-ball-0"
          aria-label="0"
          onClick={() => onChange(0)}
        >
          0
        </button>
        {dots.map((n) => {
          const cls = nearestAnchor >= n ? "full" : nearestAnchor >= n - 0.5 ? "half" : "";
          return (
            <div key={n} className={`pick-ball${cls ? ` ${cls}` : ""}`} data-n={n}>
              <button
                type="button"
                className="hit l"
                data-testid={`pick-ball-${n}-left`}
                aria-label={`${n - 0.5}`}
                onClick={() => onChange(n - 0.5)}
              />
              <button
                type="button"
                className="hit r"
                data-testid={`pick-ball-${n}-right`}
                aria-label={`${n}`}
                onClick={() => onChange(n)}
              />
            </div>
          );
        })}
      </div>
      <div className={`ball-readout${offAnchor ? " off-anchor" : ""}`}>
        <div className="ball-readout-row">
          <button
            type="button"
            className="ball-step"
            data-testid="ball-step-down"
            aria-label="diminuir 0,1"
            disabled={atMin}
            onClick={stepDown}
          >
            <BallStepChevron direction="left" />
          </button>
          <span
            data-testid="ball-readout"
            className={`ball-val${selected !== current ? " changed" : ""}`}
            role="spinbutton"
            aria-valuenow={selected}
            aria-valuemin={0}
            aria-valuemax={5}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") {
                e.preventDefault();
                stepDown();
              } else if (e.key === "ArrowRight") {
                e.preventDefault();
                stepUp();
              }
            }}
          >
            {selected.toFixed(1)}
          </span>
          <button
            type="button"
            className="ball-step"
            data-testid="ball-step-up"
            aria-label="aumentar 0,1"
            disabled={atMax}
            onClick={stepUp}
          >
            <BallStepChevron direction="right" />
          </button>
        </div>
        {offAnchor && (
          <div className="ball-anchor-track" data-testid="ball-off-anchor" aria-live="polite">
            <span className="ball-anchor-num">{lowerAnchor.toFixed(1)}</span>
            <span className="ball-anchor-line" aria-hidden>
              <span className="ball-anchor-marker" style={{ left: `${trackPct * 100}%` }} />
            </span>
            <span className="ball-anchor-num">{upperAnchor.toFixed(1)}</span>
          </div>
        )}
        <span className="ball-original">
          original <strong>{formatBall(current)}</strong>
        </span>
      </div>
    </div>
  );
}

/** Proficiency tier copy aligned with the FDE manual (Domain Architect, etc.).
 *  Surfacing the tier name above the ball picker lets the CHRO pick by concept
 *  ("Independent Judge") instead of counting stars. */
const DOT_TIER_NAMES: Record<0 | 1 | 2 | 3 | 4 | 5, string> = {
  0: "Sem proficiência",
  1: "Ciente",
  2: "Conceitual",
  3: "Pensador aplicado",
  4: "Juiz independente",
  5: "Arquiteto do domínio",
};

function DotTierHint({ selected }: { selected: number }) {
  const rounded = Math.max(0, Math.min(5, Math.round(selected))) as 0 | 1 | 2 | 3 | 4 | 5;
  return (
    <div className="ap-dot-tier" data-testid="ap-dot-tier">
      <span className="ap-dot-tier-n">{rounded}/5</span>
      <span className="ap-dot-tier-name">{DOT_TIER_NAMES[rounded]}</span>
    </div>
  );
}

function SliderPicker({ current, selected, onChange }: PickerProps) {
  const min = 0.4;
  const max = 1.0;
  const step = 0.01;
  const pct = ((current - min) / (max - min)) * 100;
  return (
    <div className="slider-picker">
      <div className="slider-rail">
        <div className="slider-track" />
        <div className="slider-orig" style={{ left: `calc(${pct}% - 1px)` }} />
        <input
          type="range"
          className="slider-input"
          min={min}
          max={max}
          step={step}
          value={selected}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      </div>
      <div className="slider-ticks">
        <span>0.40</span>
        <span>0.60</span>
        <span>0.80</span>
        <span>1.00</span>
      </div>
      <div className="slider-readout">
        <span
          data-testid="slider-readout"
          className={`slider-val${selected !== current ? " changed" : ""}`}
        >
          {selected.toFixed(2)}
        </span>
        <span className="slider-original">
          original: <strong>{current.toFixed(2)}</strong>
        </span>
      </div>
    </div>
  );
}

function LevelPicker({ current, selected, onChange }: PickerProps) {
  const levels = [1, 2, 3, 4, 5];
  return (
    <div className="value-picker">
      {levels.map((n) => {
        const isOriginal = n === current;
        const isSelected = n === selected;
        const cls = [isOriginal ? "original" : "", isSelected ? "selected" : ""]
          .filter(Boolean)
          .join(" ");
        return (
          <button
            key={n}
            type="button"
            data-val={`L${n}`}
            className={cls || undefined}
            onClick={() => onChange(n)}
          >
            L{n}
          </button>
        );
      })}
    </div>
  );
}
